import React, { useEffect, useRef } from 'react';
import { useT } from '../i18n';

// ── PER-TILE SETTINGS MENU ───────────────────────────────────────────────────
// Opened by the gear on a gallery tile. Replaces the inline eye/textarea/delete
// row, which put three controls under every thumbnail and left little room for
// the image itself.
//
// Closes on outside click and on Escape, both bound at the document level so a
// click anywhere — including on another tile's gear — dismisses it.
export default function GalleryItemMenu({ item, onDescription, onToggleVisible, onDelete, onShareToChat, onClose }) {
  const { t } = useT();
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    // `capture` so the handler runs before the gear's own onClick can re-open it.
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const rows = [
    // PUBLISHED ITEMS ONLY. A share card opens the owner's public profile at
    // this item, and that listing is visible-only — offering it on a hidden
    // item would send someone a link to a page that does not show it.
    item.visible && onShareToChat && {
      key: 'to-chat',
      label: t('share_to_chat'),
      icon: '💬',
      onClick: () => { onShareToChat(); onClose(); },
    },
    {
      key: 'visible',
      label: item.visible ? t('gal_menu_unpublish') : t('gal_menu_publish'),
      icon: item.visible ? '🚫' : '👁',
      onClick: () => { onToggleVisible(); onClose(); },
    },
    {
      key: 'description',
      label: t('gal_menu_description'),
      icon: '✎',
      onClick: () => { onDescription(); onClose(); },
    },
    {
      key: 'delete',
      label: t('gal_delete'),
      icon: '🗑',
      danger: true,
      onClick: () => { onDelete(); onClose(); },
    },
  ].filter(Boolean);

  return (
    <div className="gal-menu" ref={ref} role="menu">
      {rows.map(r => (
        <button
          key={r.key}
          type="button"
          role="menuitem"
          className={`gal-menu-item${r.danger ? ' gal-menu-item--danger' : ''}`}
          onClick={r.onClick}
        >
          <span className="gal-menu-icon" aria-hidden="true">{r.icon}</span>
          {r.label}
        </button>
      ))}
    </div>
  );
}
